/** Connect catalogue client plus the retained REST tree/node compatibility surface. */
import { readCsrfCookie } from './boardsClient.js';
import { createClient, Code, ConnectError, type Client, type Interceptor } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  ProjectErrorDetailSchema,
  ProjectListView as WireProjectListView,
  ProjectRole,
  ProjectService,
  type Project,
} from '../../../gocanvas/gen/ts-connect/gocanvas/v1/api_pb.js';

export interface TreeNode {
  id: string;
  parent_id?: string;
  sort_key: string;
  kind: 'board' | 'page';
  title: string;
  depth: number;
  icon_key?: ProjectTreeIconKey;
}

export type ProjectTreeIconKey = 'diamond' | 'circle' | 'sparkle' | 'panel';

export interface ProjectTreeNodeInfo {
  id: string;
  kind: TreeNode['kind'];
  title: string;
  path: string[];
  created_at: string;
  updated_at: string;
  icon_key?: ProjectTreeIconKey;
  owner?: string;
}

export interface PageDescriptor {
  id: string;
  project_id: string;
  title: string;
  effective_role: EffectiveRole;
  parent_id?: string;
  sort_key?: string;
}

export type EffectiveRole = 'owner' | 'editor' | 'commenter' | 'viewer' | 'unknown';

export interface ProjectSummary {
  id: string;
  owner_id: string;
  title: string;
  description: string;
  is_personal: boolean;
  design_system_id?: string;
  created_at: string;
  updated_at: string;
  effective_role: EffectiveRole;
}

export type ProjectListView = 'recent' | 'catalogue-stable';

export interface ProjectPage {
  projects: readonly ProjectSummary[];
  nextPageToken: string;
}

export interface ProjectsClientOptions {
  /** Defaults to `globalThis.fetch`. Tests inject a `vi.fn()`. */
  fetchImpl?: typeof fetch;
  connectClient?: ProjectConnectClient;
  /** Test seam only. Production Connect always uses the browser origin. */
  connectBase?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export type ProjectConnectClient = Client<typeof ProjectService>;

export class ProjectsClientError extends Error {
  readonly status: number;
  readonly detailCode?: string;
  readonly retryAfterMs?: number;
  constructor(status: number, message: string, detailCode?: string, retryAfterMs?: number) {
    super(message);
    this.status = status;
    if (detailCode !== undefined) this.detailCode = detailCode;
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs;
    this.name = 'ProjectsClientError';
  }
}

export function createProjectConnectClient(
  apiBase: string,
  opts: Pick<ProjectsClientOptions, 'fetchImpl' | 'connectBase'> = {},
): ProjectConnectClient {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const connectBase = trimBase(opts.connectBase ?? window.location.origin ?? apiBase);
  const csrfMutation: Interceptor = (next) => async (req) => {
    if (req.method.name === 'BootstrapPersonalProject' || req.method.name === 'CreateProject') {
      let csrf = readCsrfCookie();
      if (!csrf) {
        const response = await fetchImpl(`${connectBase}/v1/auth/csrf`, {
          method: 'GET', credentials: 'include', signal: req.signal, redirect: 'error',
        });
        if (!response.ok) throw new ConnectError('CSRF initialization failed', Code.Unavailable);
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null || !('csrf_token' in body)
          || typeof body.csrf_token !== 'string' || body.csrf_token === '') {
          throw new ConnectError('CSRF response is malformed', Code.Internal);
        }
        csrf = body.csrf_token;
      }
      req.header.set('X-CSRF-Token', csrf);
    }
    // No Authorization header is sourced from localStorage. Browser auth
    // authority is the verified HttpOnly cookie sent by credentials=include.
    return next(req);
  };
  const transport = createConnectTransport({
    baseUrl: `${connectBase}/rpc`,
    useHttpGet: true,
    defaultTimeoutMs: 10_000,
    interceptors: [csrfMutation],
    fetch: (input, init) => fetchImpl(input, { ...init, credentials: 'include' }),
  });
  return createClient(ProjectService, transport);
}

function projectSummary(project: Project): ProjectSummary {
  if (project.createdAt === undefined || project.updatedAt === undefined) {
    throw new ProjectsClientError(502, 'project response is missing timestamps', 'project.response_malformed');
  }
  return {
    id: bytesToHex(project.id),
    owner_id: bytesToHex(project.ownerId),
    title: project.title,
    description: project.description,
    is_personal: project.isPersonal,
    ...(project.designSystemId !== undefined && { design_system_id: project.designSystemId }),
    created_at: timestampIso(project.createdAt),
    updated_at: timestampIso(project.updatedAt),
    effective_role: effectiveRole(project.effectiveRole),
  };
}

function timestampIso(value: NonNullable<Project['createdAt']>): string {
  try {
    return timestampDate(value).toISOString();
  } catch {
    throw new ProjectsClientError(502, 'project response contains an invalid timestamp', 'project.response_malformed');
  }
}

function effectiveRole(role: ProjectRole): EffectiveRole {
  switch (role) {
    case ProjectRole.OWNER:
      return 'owner';
    case ProjectRole.EDITOR:
      return 'editor';
    case ProjectRole.COMMENTER:
      return 'commenter';
    case ProjectRole.VIEWER:
      return 'viewer';
    default:
      return 'unknown';
  }
}

function bytesToHex(value: Uint8Array): string {
  if (value.byteLength !== 16) {
    throw new ProjectsClientError(502, 'project response contains a malformed id', 'project.response_malformed');
  }
  return Array.from(value, (part) => part.toString(16).padStart(2, '0')).join('');
}

export function isProjectNodeId(value: string): boolean {
  return /^[0-9a-f]{32}$/u.test(value);
}

function hexToBytes(value: string): Uint8Array {
  if (!/^[0-9a-fA-F]{32}$/.test(value)) {
    throw new ProjectsClientError(400, 'project id must be 32 hexadecimal characters', 'project.invalid_id');
  }
  return Uint8Array.from(value.match(/.{2}/g) ?? [], (part) => Number.parseInt(part, 16));
}

function connectStatus(code: Code): number {
  switch (code) {
    case Code.InvalidArgument:
      return 400;
    case Code.Unauthenticated:
      return 401;
    case Code.PermissionDenied:
      return 403;
    case Code.AlreadyExists:
      return 409;
    case Code.NotFound:
      return 404;
    case Code.Canceled:
      return 499;
    case Code.DeadlineExceeded:
      return 408;
    case Code.ResourceExhausted:
      return 429;
    case Code.Internal:
      return 500;
    case Code.Unavailable:
      return 503;
    default:
      return 0;
  }
}

function mapConnectError(reason: unknown, operation: string): ProjectsClientError {
  if (reason instanceof ProjectsClientError) return reason;
  const err = ConnectError.from(reason);
  const detailCode = err.findDetails(ProjectErrorDetailSchema)[0]?.code;
  const retryAfterMs = parseRetryAfter(err.metadata.get('Retry-After'));
  return new ProjectsClientError(
    connectStatus(err.code),
    `${operation}: ${err.rawMessage}`,
    detailCode,
    retryAfterMs,
  );
}

function parseRetryAfter(value: string | null): number | undefined {
  if (value === null) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const deadline = Date.parse(value);
  if (!Number.isFinite(deadline)) return undefined;
  return Math.max(0, deadline - Date.now());
}

function callOptions(opts: ProjectsClientOptions): { timeoutMs?: number; signal?: AbortSignal } | undefined {
  if (opts.timeoutMs === undefined && opts.signal === undefined) return undefined;
  return {
    ...(opts.timeoutMs !== undefined && { timeoutMs: opts.timeoutMs }),
    ...(opts.signal !== undefined && { signal: opts.signal }),
  };
}

function trimBase(apiBase: string): string {
  return apiBase.replace(/\/+$/, '');
}

async function request(
  apiBase: string,
  method: string,
  path: string,
  opts: ProjectsClientOptions,
  body?: unknown,
  csrfToken?: string,
): Promise<Response> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCsrfCookie() || csrfToken;
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  // `exactOptionalPropertyTypes` is on: an explicit `body: undefined` is NOT
  // the same as an absent `body`, and RequestInit does not admit undefined.
  // Build the init object and only attach `body` when there is one.
  const abort = requestAbort(opts);
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal: abort.signal,
  };
  if (path === '/v1/auth/csrf') init.redirect = 'error';
  if (body !== undefined) init.body = JSON.stringify(body);
  try {
    return await fetchImpl(`${trimBase(apiBase)}${path}`, init);
  } catch (reason) {
    if (abort.didTimeout()) {
      throw new ProjectsClientError(408, `${method} ${path} timed out`);
    }
    throw reason;
  } finally {
    abort.cleanup();
  }
}

const REST_TIMEOUT_MS = 10_000;

function requestAbort(opts: ProjectsClientOptions): {
  signal: AbortSignal;
  didTimeout: () => boolean;
  cleanup: () => void;
} {
  const controller = new AbortController();
  let timedOut = false;
  const timeout = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, opts.timeoutMs ?? REST_TIMEOUT_MS);
  const forwardAbort = (): void => {
    controller.abort(opts.signal?.reason ?? new DOMException('Aborted', 'AbortError'));
  };
  if (opts.signal?.aborted === true) forwardAbort();
  else opts.signal?.addEventListener('abort', forwardAbort, { once: true });
  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      globalThis.clearTimeout(timeout);
      opts.signal?.removeEventListener('abort', forwardAbort);
    },
  };
}

export async function listProjectsPage(
  apiBase: string,
  requestPage: { view: ProjectListView; pageSize: number; pageToken: string },
  opts: ProjectsClientOptions = {},
): Promise<ProjectPage> {
  const client = opts.connectClient ?? createProjectConnectClient(apiBase, opts);
  try {
    const response = await client.listProjects({
      pageSize: requestPage.pageSize,
      pageToken: requestPage.pageToken,
      view: requestPage.view === 'recent'
        ? WireProjectListView.RECENT
        : WireProjectListView.CATALOGUE_STABLE,
    }, callOptions(opts));
    return {
      projects: response.projects.map(projectSummary),
      nextPageToken: response.nextPageToken,
    };
  } catch (err) {
    throw mapConnectError(err, 'listProjects');
  }
}

const MAX_CATALOGUE_PAGES = 1_000;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+$/;

export async function listAllProjectsStable(
  apiBase: string,
  opts: ProjectsClientOptions = {},
): Promise<ProjectSummary[]> {
  const projects = new Map<string, ProjectSummary>();
  const cursors = new Set<string>();
  let pageToken = '';

  for (let page = 0; page < MAX_CATALOGUE_PAGES; page += 1) {
    const result = await listProjectsPage(apiBase, {
      view: 'catalogue-stable',
      pageSize: 100,
      pageToken,
    }, opts);
    for (const project of result.projects) {
      if (!projects.has(project.id)) projects.set(project.id, project);
    }
    const next = result.nextPageToken;
    if (next === '') return [...projects.values()];
    if (result.projects.length === 0) {
      throw new ProjectsClientError(502, 'server returned an empty nonterminal page', 'project.page_malformed');
    }
    if (!CURSOR_PATTERN.test(next)) {
      throw new ProjectsClientError(502, 'server returned a malformed project cursor', 'project.cursor_invalid');
    }
    if (cursors.has(next)) {
      throw new ProjectsClientError(502, 'server repeated a project cursor', 'project.cursor_repeated');
    }
    cursors.add(next);
    pageToken = next;
  }
  throw new ProjectsClientError(502, 'project catalogue exceeded the page safety bound', 'project.page_bound');
}

/** Temporary compatibility for the old page until Task 6 migrates its entry. */
export async function listProjects(
  apiBase: string,
  opts: ProjectsClientOptions = {},
): Promise<ProjectSummary[]> {
  return listAllProjectsStable(apiBase, opts);
}

export async function bootstrapPersonalProject(
  apiBase: string,
  opts: ProjectsClientOptions = {},
): Promise<{ project: ProjectSummary; starterBoardId: string }> {
  const client = opts.connectClient ?? createProjectConnectClient(apiBase, opts);
  try {
    const response = await client.bootstrapPersonalProject({}, callOptions(opts));
    if (response.project === undefined || response.starterBoard === undefined) {
      throw new ProjectsClientError(
        502,
        'bootstrapPersonalProject: incomplete response',
        'project.response_malformed',
      );
    }
    return {
      project: projectSummary(response.project),
      starterBoardId: bytesToHex(response.starterBoard.id),
    };
  } catch (err) {
    if (err instanceof ProjectsClientError) throw err;
    throw mapConnectError(err, 'bootstrapPersonalProject');
  }
}

export async function getProject(
  apiBase: string,
  projectId: string,
  opts: ProjectsClientOptions = {},
): Promise<ProjectSummary> {
  const client = opts.connectClient ?? createProjectConnectClient(apiBase, opts);
  try {
    const response = await client.getProject({ projectId: hexToBytes(projectId) }, callOptions(opts));
    if (response.project === undefined) {
      throw new ProjectsClientError(502, 'getProject returned no project', 'project.response_malformed');
    }
    return projectSummary(response.project);
  } catch (err) {
    throw mapConnectError(err, 'getProject');
  }
}

export async function createProject(
  apiBase: string,
  input: { title: string; idempotencyKey: string },
  opts: ProjectsClientOptions = {},
): Promise<ProjectSummary> {
  const client = opts.connectClient ?? createProjectConnectClient(apiBase, opts);
  try {
    const response = await client.createProject(input, callOptions(opts));
    if (response.project === undefined) {
      throw new ProjectsClientError(502, 'createProject returned no project', 'project.response_malformed');
    }
    return projectSummary(response.project);
  } catch (err) {
    throw mapConnectError(err, 'createProject');
  }
}

export async function getTree(
  apiBase: string,
  projectId: string,
  opts: ProjectsClientOptions = {},
): Promise<TreeNode[]> {
  const resp = await request(
    apiBase,
    'GET',
    `/v1/projects/${encodeURIComponent(projectId)}/tree`,
    opts,
  );
  if (!resp.ok) throw new ProjectsClientError(resp.status, `getTree ${resp.status}`);
  const body = (await resp.json()) as { nodes?: TreeNode[] };
  return body.nodes ?? [];
}

export async function createPage(
  apiBase: string,
  projectId: string,
  input: { idempotencyKey: string; snapshot: Uint8Array; parentId?: string; title?: string },
  opts: ProjectsClientOptions = {},
): Promise<PageDescriptor> {
  let csrf = readCsrfCookie();
  if (!csrf) {
    const response = await request(apiBase, 'GET', '/v1/auth/csrf', opts);
    if (!response.ok) throw await nodeMutationError(response, 'initializePageCsrf');
    const body: unknown = await response.json().catch(() => null);
    if (typeof body !== 'object' || body === null || !('csrf_token' in body)
      || typeof body.csrf_token !== 'string' || body.csrf_token === '') {
      throw new ProjectsClientError(502, 'CSRF response is malformed', 'auth.csrf.invalid_response');
    }
    csrf = body.csrf_token;
  }
  const resp = await request(
    apiBase,
    'POST',
    `/v1/projects/${encodeURIComponent(projectId)}/pages`,
    opts,
    {
      idempotency_key: input.idempotencyKey,
      snapshot: bytesToBase64(input.snapshot),
      ...(input.parentId === undefined ? {} : { parent_id: input.parentId }),
      ...(input.title === undefined ? {} : { title: input.title }),
    },
    csrf,
  );
  if (!resp.ok) throw await nodeMutationError(resp, 'createPage');
  return (await resp.json()) as PageDescriptor;
}

export async function getPage(
  apiBase: string,
  pageId: string,
  opts: ProjectsClientOptions = {},
): Promise<PageDescriptor> {
  const resp = await request(
    apiBase,
    'GET',
    `/v1/pages/${encodeURIComponent(pageId)}`,
    opts,
  );
  if (!resp.ok) throw new ProjectsClientError(resp.status, `getPage ${resp.status}`);
  return (await resp.json()) as PageDescriptor;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis.btoa(binary);
}

export async function patchNode(
  apiBase: string,
  nodeId: string,
  patch: {
    title?: string;
    parentId?: string;
    sortKey?: string;
    beforeNodeId?: string;
    afterNodeId?: string;
  },
  opts: ProjectsClientOptions = {},
): Promise<TreeNode> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body.title = patch.title;
  if (patch.parentId !== undefined) body.parent_id = patch.parentId;
  if (patch.sortKey !== undefined) body.sort_key = patch.sortKey;
  if (patch.beforeNodeId !== undefined) body.before_node_id = patch.beforeNodeId;
  if (patch.afterNodeId !== undefined) body.after_node_id = patch.afterNodeId;
  const resp = await request(
    apiBase,
    'PATCH',
    `/v1/nodes/${encodeURIComponent(nodeId)}`,
    opts,
    body,
  );
  if (!resp.ok) throw await nodeMutationError(resp, 'patchNode');
  return (await resp.json()) as TreeNode;
}

async function nodeMutationError(resp: Response, operation: string): Promise<ProjectsClientError> {
  let detailCode: string | undefined;
  if (resp.headers.get('content-type')?.includes('application/problem+json') === true) {
    try {
      const problem = (await resp.json()) as { code?: unknown };
      if (typeof problem.code === 'string') detailCode = problem.code;
    } catch {
      // A malformed error body must not hide the authoritative HTTP status.
    }
  }
  return new ProjectsClientError(resp.status, `${operation} ${resp.status}`, detailCode);
}

export async function deleteNode(
  apiBase: string,
  nodeId: string,
  opts: ProjectsClientOptions = {},
): Promise<void> {
  const resp = await request(apiBase, 'DELETE', `/v1/nodes/${encodeURIComponent(nodeId)}`, opts);
  if (!resp.ok && resp.status !== 204) {
    throw new ProjectsClientError(resp.status, `deleteNode ${resp.status}`);
  }
}

/** Restore a just-archived project item through the cookie + CSRF REST boundary. */
export async function restoreNode(
  apiBase: string,
  nodeId: string,
  opts: ProjectsClientOptions = {},
): Promise<void> {
  const resp = await request(
    apiBase,
    'POST',
    `/v1/nodes/${encodeURIComponent(nodeId)}/restore`,
    opts,
  );
  if (resp.status !== 204) {
    throw new ProjectsClientError(resp.status, `restoreNode ${resp.status}`);
  }
}

export async function duplicateNode(
  apiBase: string,
  nodeId: string,
  input: {
    idempotencyKey: string;
    page?: {
      sourceTitle: string;
      title: string;
      titleUpdate: Uint8Array;
      sourceEpoch: Uint8Array;
      sourceVersion: bigint;
    };
  },
  opts: ProjectsClientOptions = {},
): Promise<TreeNode> {
  const resp = await request(
    apiBase,
    'POST',
    `/v1/nodes/${encodeURIComponent(nodeId)}/duplicate`,
    opts,
    {
      idempotency_key: input.idempotencyKey,
      ...(input.page === undefined ? {} : {
        source_title: input.page.sourceTitle,
        title: input.page.title,
        title_update: bytesToBase64(input.page.titleUpdate),
        source_epoch: bytesToBase64(input.page.sourceEpoch),
        source_version: input.page.sourceVersion.toString(),
      }),
    },
  );
  if (!resp.ok) throw await nodeMutationError(resp, 'duplicateNode');
  return (await resp.json()) as TreeNode;
}

export async function getNodeInfo(
  apiBase: string,
  nodeId: string,
  opts: ProjectsClientOptions = {},
): Promise<ProjectTreeNodeInfo> {
  const resp = await request(apiBase, 'GET', `/v1/nodes/${encodeURIComponent(nodeId)}`, opts);
  if (!resp.ok) throw new ProjectsClientError(resp.status, `getNodeInfo ${resp.status}`);
  return (await resp.json()) as ProjectTreeNodeInfo;
}

export async function setNodeIcon(
  apiBase: string,
  nodeId: string,
  iconKey: ProjectTreeIconKey | null,
  opts: ProjectsClientOptions = {},
): Promise<TreeNode> {
  const resp = await request(
    apiBase,
    iconKey === null ? 'DELETE' : 'PUT',
    `/v1/nodes/${encodeURIComponent(nodeId)}/icon`,
    opts,
    iconKey === null ? undefined : { icon_key: iconKey },
  );
  if (!resp.ok) throw new ProjectsClientError(resp.status, `setNodeIcon ${resp.status}`);
  return (await resp.json()) as TreeNode;
}
